

import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/analytics";
import { Loader2, AlertTriangle, Star, Mail, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ViolationBadge } from "./ViolationBadge";
import { ConfidenceBar } from "./ConfidenceBar";

type Violation = {
  type: string;
  policyClause: string;
  confidence: number;
  evidence?: string;
};

type ActionStatus = "none" | "watching" | "authorized" | "submitted" | "removed" | "rejected";

type Review = {
  id: string;
  asin: string;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  reviewDate: string | null;
  verifiedPurchase: boolean;
  classification: {
    violations: Violation[];
    flaggedForTakedown: boolean;
    estimatedImpactUsd: number | null;
    rationale: string | null;
    actionStatus: ActionStatus;
  } | null;
};

type Verdict =
  | "pending"
  | "clean"
  | "flagged"
  | "watching"
  | "authorized"
  | "submitted"
  | "removed"
  | "rejected";

function verdictOf(r: Review): Verdict {
  if (!r.classification) return "pending";
  const a = r.classification.actionStatus;
  if (a === "removed") return "removed";
  if (a === "submitted") return "submitted";
  if (a === "authorized") return "authorized";
  if (a === "watching") return "watching";
  if (a === "rejected") return "rejected";
  return r.classification.flaggedForTakedown ? "flagged" : "clean";
}

const PILL_STYLES: Record<Verdict, { cls: string; label: string }> = {
  pending: {
    cls: "bg-slate-100 text-slate-700 ring-slate-200",
    label: "Pending",
  },
  clean: {
    cls: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    label: "Clean",
  },
  flagged: {
    cls: "bg-amber-50 text-amber-800 ring-amber-200",
    label: "Flagged",
  },
  watching: {
    cls: "bg-slate-100 text-slate-700 ring-slate-200",
    label: "Watching",
  },
  authorized: {
    cls: "bg-sky-50 text-sky-700 ring-sky-200",
    label: "Authorized · awaiting filing",
  },
  submitted: {
    cls: "bg-blue-50 text-blue-700 ring-blue-200",
    label: "Submitted",
  },
  removed: {
    cls: "bg-emerald-100 text-emerald-800 ring-emerald-300",
    label: "Removed",
  },
  rejected: {
    cls: "bg-red-50 text-red-700 ring-red-200",
    label: "Rejected",
  },
};

function StatusPill({ verdict }: { verdict: Verdict }) {
  const { cls, label } = PILL_STYLES[verdict];
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
}

type AuditData = {
  id: string;
  status: "queued" | "scraping" | "classifying" | "completed" | "failed";
  email: string;
  inputAsins: string[];
  scrapedReviewCount: number;
  flaggedCount: number;
  createdAt: string;
  reviews: Review[];
};

export function AuditResults({ auditId }: { auditId: string }) {
  const [data, setData] = useState<AuditData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const completedFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function tick() {
      try {
        const res = await fetch(`/api/audit/${auditId}`, { cache: "no-store" });
        if (!res.ok) {
          setError(res.status === 404 ? "Audit not found." : "Failed to load audit.");
          return;
        }
        const json = (await res.json()) as AuditData;
        if (cancelled) return;
        setData(json);
        if (json.status === "completed" && !completedFiredRef.current) {
          completedFiredRef.current = true;
          const flaggedCount = json.reviews.filter(
            (r) => r.classification?.flaggedForTakedown
          ).length;
          track("audit_completed", {
            audit_id: json.id,
            flagged_count: flaggedCount,
            scraped_count: json.scrapedReviewCount,
          });
        }
        if (json.status !== "completed" && json.status !== "failed") {
          setTimeout(tick, 3000);
        }
      } catch {
        if (!cancelled) setTimeout(tick, 5000);
      }
    }

    tick();
    return () => {
      cancelled = true;
    };
  }, [auditId]);

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-3 text-slate-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading…
      </div>
    );
  }

  const inProgress =
    data.status === "queued" ||
    data.status === "scraping" ||
    data.status === "classifying";

  const flagged = data.reviews.filter((r) => r.classification?.flaggedForTakedown);
  const removed = data.reviews.filter(
    (r) => r.classification?.actionStatus === "removed"
  );
  const submitted = data.reviews.filter(
    (r) => r.classification?.actionStatus === "submitted"
  );
  const pendingAction = flagged.filter((r) => {
    const a = r.classification?.actionStatus ?? "none";
    return a === "none" || a === "watching" || a === "rejected";
  });

  return (
    <div>
      <div className="mb-8">
        <div className="text-sm text-slate-500">Audit results</div>
        <h1 className="mt-1 text-3xl md:text-4xl font-semibold tracking-tight">
          {data.inputAsins.length > 0
            ? `${data.inputAsins.length} ASIN${data.inputAsins.length === 1 ? "" : "s"} analyzed`
            : "Storefront analyzed"}
        </h1>
        {data.status === "completed" && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span>{data.scrapedReviewCount} reviews analyzed</span>
            {removed.length > 0 && (
              <>
                <span>·</span>
                <span className="font-medium text-emerald-700">
                  {removed.length} removed
                </span>
              </>
            )}
            {submitted.length > 0 && (
              <>
                <span>·</span>
                <span className="font-medium text-blue-700">
                  {submitted.length} submitted
                </span>
              </>
            )}
            {pendingAction.length > 0 && (
              <>
                <span>·</span>
                <span className="font-medium text-slate-900">
                  {pendingAction.length} likely removable
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {inProgress && (
        <>
          <div className="mb-6 flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-slate-700">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <div className="text-sm">
              <div className="font-medium text-slate-900">
                Thanks — your audit is in our queue.
              </div>
              <div className="mt-1 text-slate-600">
                We&rsquo;ll send you an email with your results, usually within 24 hours.
              </div>
            </div>
          </div>

          <div className="mb-10 rounded-xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-medium text-slate-900">
              While you wait
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Want a quick walkthrough or have questions about your catalog?
              Grab 15 minutes — same-day slots usually open.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <a
                href="https://cal.com/maximiliang/demo"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Book a 15-min call
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`mailto:audits@reputely.co?subject=${encodeURIComponent(
                  `Question about my audit (${data.id.slice(0, 8)})`
                )}&body=${encodeURIComponent(
                  `Hi,\n\nI just submitted an audit (id: ${data.id}) and have a question:\n\n`
                )}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900"
              >
                Email us instead
              </a>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900"
              >
                How it works
              </Link>
            </div>
          </div>
        </>
      )}

      {data.status === "failed" && (
        <div className="mb-8 flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm">
            Audit failed. Please try again or contact support.
          </span>
        </div>
      )}

      {pendingAction.length > 0 && (
        <ConversionCard
          variant="flagged"
          flaggedCount={pendingAction.length}
          auditId={data.id}
        />
      )}

      {data.status === "completed" &&
        flagged.length === 0 &&
        removed.length === 0 && (
          <ConversionCard variant="clean" auditId={data.id} />
        )}

      <ul className="space-y-4">
        {data.reviews.map((r) => (
          <ReviewCard key={r.id} review={r} />
        ))}
      </ul>
    </div>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const flagged = review.classification?.flaggedForTakedown;
  const verdict = verdictOf(review);
  const cardClass =
    verdict === "removed"
      ? "rounded-xl border border-emerald-200 bg-emerald-50/30 p-5"
      : verdict === "submitted"
        ? "rounded-xl border border-blue-200 bg-blue-50/30 p-5"
        : verdict === "rejected"
          ? "rounded-xl border border-red-200 bg-red-50/30 p-5"
          : flagged
            ? "rounded-xl border border-amber-200 bg-amber-50/20 p-5"
            : "rounded-xl border border-slate-200 bg-white p-5";
  return (
    <li className={cardClass}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            {verdict !== "pending" && verdict !== "clean" && (
              <StatusPill verdict={verdict} />
            )}
            <span className="font-mono text-xs">{review.asin}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={
                    i < review.rating
                      ? "h-3.5 w-3.5 fill-amber-400 text-amber-400"
                      : "h-3.5 w-3.5 text-slate-300"
                  }
                />
              ))}
            </span>
            {review.verifiedPurchase && (
              <span className="text-xs text-emerald-700">Verified</span>
            )}
            {review.reviewerName && (
              <>
                <span>·</span>
                <span className="truncate">{review.reviewerName}</span>
              </>
            )}
          </div>
          {review.title && (
            <div className="mt-2 font-medium text-slate-900">{review.title}</div>
          )}
          <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {review.body}
          </p>
          {flagged && review.classification && review.classification.violations.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {review.classification.violations.map((v, i) => (
                <ViolationBadge key={i} type={v.type} />
              ))}
            </div>
          )}
          {flagged && review.classification && review.classification.violations.length === 0 && (
            <p className="mt-3 text-xs text-slate-600">
              Flagged by our reviewer.
            </p>
          )}
        </div>
        {flagged && review.classification && review.classification.violations[0] && (
          <div className="shrink-0">
            <ConfidenceBar value={review.classification.violations[0].confidence} />
          </div>
        )}
      </div>
    </li>
  );
}

function ConversionCard({
  variant,
  flaggedCount,
  auditId,
}: {
  variant: "flagged" | "clean";
  flaggedCount?: number;
  auditId: string;
}) {
  const isFlagged = variant === "flagged";
  const headline = isFlagged
    ? `${flaggedCount} review${flaggedCount === 1 ? "" : "s"} look${flaggedCount === 1 ? "s" : ""} removable.`
    : "Your catalog looks clean.";
  const subhead = isFlagged
    ? "Start monitoring before the next one lands."
    : "Want to keep it that way?";
  const body = isFlagged
    ? "Reputely continuously watches your catalog, prepares appeal-ready submissions, and alerts your team when reviews violate Amazon policy."
    : "We watch every new review as it comes in and flag violations within hours. Plans start at $79/month.";
  const trust = [
    "We draft the takedown request",
    "Submitted through Amazon's official channels",
    "Refund if it doesn't come down",
  ];
  const mailto = `mailto:audits@reputely.co?subject=${encodeURIComponent(
    `Question about my audit (${auditId.slice(0, 8)})`
  )}&body=${encodeURIComponent(
    `Hi,\n\nI just ran an audit (id: ${auditId}) and have a question:\n\n`
  )}`;

  return (
    <div className="mb-10 rounded-xl border border-slate-900 bg-slate-900 text-white p-6 md:p-8">
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
        {headline}
        <br />
        <span className="text-slate-300 font-medium">{subhead}</span>
      </h2>
      <p className="mt-4 text-sm md:text-base text-slate-300 max-w-xl">
        {body}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {isFlagged && (
          <Link
            to={`/start-trial?auditId=${auditId}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
          >
            Start free trial →
          </Link>
        )}
        <Link
          to="/pricing"
          className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium transition ${
            isFlagged
              ? "bg-slate-700 text-white hover:bg-slate-600"
              : "bg-white text-slate-900 hover:bg-slate-100"
          }`}
        >
          {isFlagged ? "Compare plans" : "Start monitoring from $79/mo"}
        </Link>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <a
          href="https://cal.com/maximiliang/demo"
          target="_blank"
          rel="noreferrer"
          className="text-slate-300 hover:text-white"
        >
          Book a call
        </a>
        <a href={mailto} className="text-slate-300 hover:text-white">
          Email us
        </a>
        <Link to="/pricing" className="text-slate-300 hover:text-white">
          See pricing
        </Link>
        <Link to="/how-it-works" className="text-slate-300 hover:text-white">
          How it works
        </Link>
      </div>

      {isFlagged && (
        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-400">
          {trust.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <span className="text-emerald-400">✓</span>
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
